import React, { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Camera, MapPin, Clock, LogIn, LogOut } from "lucide-react";

const getLocation = () => new Promise((res, rej) => {
  if (!navigator.geolocation) return rej(new Error("Geolokasi tidak didukung"));
  navigator.geolocation.getCurrentPosition(
    (pos) => res({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
    (err) => rej(err),
    { enableHighAccuracy: true, timeout: 15000 }
  );
});

const reverseGeocode = async (lat, lng) => {
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
    const d = await r.json();
    return d.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  } catch { return `${lat.toFixed(5)}, ${lng.toFixed(5)}`; }
};

export default function Attendance() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [wh, setWh] = useState(null);
  const [sched, setSched] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const inRef = useRef();
  const outRef = useRef();

  const load = async () => {
    const { data } = await api.get("/attendance/today");
    setToday(data && data.id ? data : null);
  };

  useEffect(() => {
    load();
    api.get("/settings/work-hours").then(({ data }) => setWh(data));
    api.get("/attendance/my-schedule").then(({ data }) => setSched(data));
  }, []);

  const doClock = async (kind, file) => {
    setLoading(true);
    const t = toast.loading("Mengambil lokasi GPS...");
    let lat = 0, lng = 0, address = "", noGps = false;
    try {
      const loc = await getLocation();
      lat = loc.lat; lng = loc.lng;
      toast.loading("Mencari alamat...", { id: t });
      address = await reverseGeocode(lat, lng);
    } catch {
      noGps = true;
      toast.loading("GPS tidak aktif — absen tetap dilanjutkan tanpa lokasi...", { id: t });
    }
    try {
      toast.loading("Mengunggah foto...", { id: t });
      const fd = new FormData();
      fd.append("lat", lat); fd.append("lng", lng); fd.append("address", address);
      fd.append("selfie", file);
      const url = kind === "in" ? "/attendance/clock-in" : "/attendance/clock-out";
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(
        (kind === "in" ? "Absen masuk berhasil!" : "Absen keluar berhasil!") + (noGps ? " (tanpa GPS — Bos diberi tahu)" : ""),
        { id: t }
      );
      await load();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Gagal absen", { id: t });
    } finally { setLoading(false); }
  };

  const hasIn = !!today?.clock_in_at;
  const hasOut = !!today?.clock_out_at;

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Kehadiran</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Absensi Hari Ini</h1>
        <p className="text-sm text-slate-600 mt-1">{new Date().toLocaleDateString("id-ID", { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</p>
        {sched && (
          <p className="text-xs text-slate-500 mt-1">Jam masuk {sched.location_label}: <b className="text-slate-700">{sched.scheduled_in} WIB</b>{Number(sched.tolerance_minutes) > 0 ? ` · toleransi ${sched.tolerance_minutes} menit` : ""}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Clock In */}
        <div className={`bg-white rounded-xl border-2 p-5 ${hasIn ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><LogIn size={20} /></div>
            {hasIn && <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider">Sudah</span>}
          </div>
          <h3 className="mt-3 text-lg font-bold text-slate-900" style={{fontFamily:'Manrope'}}>Absen Masuk</h3>
          {hasIn ? (
            <div className="mt-3 space-y-2">
              {today.clock_in_photo_id && <img src={fileUrl(today.clock_in_photo_id)} alt="selfie" className="w-full h-40 rounded-lg object-cover border border-slate-200" />}
              <p className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12}/> {new Date(today.clock_in_at).toLocaleTimeString('id-ID')}</p>
              {today.is_late ? (
                <span className="inline-block px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-xs font-bold uppercase tracking-wider">Telat {today.late_minutes} menit (jadwal {today.scheduled_in})</span>
              ) : (
                <span className="inline-block px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider">Tepat Waktu</span>
              )}
              {today.clock_in_no_gps && <span className="inline-block px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wider">Tanpa GPS</span>}
              <p className="text-xs text-slate-500 flex items-start gap-1"><MapPin size={12} className="mt-0.5 flex-shrink-0"/> <span className="line-clamp-2">{today.clock_in_address || "Lokasi tidak terekam"}</span></p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600 mt-1">Ambil selfie + GPS otomatis</p>
              <input ref={inRef} data-testid="clock-in-input" type="file" accept="image/*" capture="user" hidden onChange={(e) => e.target.files[0] && doClock("in", e.target.files[0])} />
              <button data-testid="clock-in-btn" disabled={loading} onClick={() => inRef.current.click()} className="mt-4 w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50">
                <Camera size={18} /> Absen Masuk
              </button>
            </>
          )}
        </div>

        {/* Clock Out */}
        <div className={`bg-white rounded-xl border-2 p-5 ${hasOut ? 'border-red-200 bg-red-50/50' : 'border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="w-11 h-11 rounded-lg bg-red-100 text-red-700 flex items-center justify-center"><LogOut size={20} /></div>
            {hasOut && <span className="px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-bold uppercase tracking-wider">Sudah</span>}
          </div>
          <h3 className="mt-3 text-lg font-bold text-slate-900" style={{fontFamily:'Manrope'}}>Absen Keluar</h3>
          {hasOut ? (
            <div className="mt-3 space-y-2">
              {today.clock_out_photo_id && <img src={fileUrl(today.clock_out_photo_id)} alt="selfie" className="w-full h-40 rounded-lg object-cover border border-slate-200" />}
              <p className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12}/> {new Date(today.clock_out_at).toLocaleTimeString('id-ID')}</p>
              {today.clock_out_no_gps && <span className="inline-block px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wider">Tanpa GPS</span>}
              <p className="text-xs text-slate-500 flex items-start gap-1"><MapPin size={12} className="mt-0.5 flex-shrink-0"/> <span className="line-clamp-2">{today.clock_out_address || "Lokasi tidak terekam"}</span></p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600 mt-1">{hasIn ? "Selesai kerja? Absen keluar." : "Absen masuk dulu."}</p>
              <input ref={outRef} data-testid="clock-out-input" type="file" accept="image/*" capture="user" hidden onChange={(e) => e.target.files[0] && doClock("out", e.target.files[0])} />
              <button data-testid="clock-out-btn" disabled={!hasIn || loading} onClick={() => outRef.current.click()} className="mt-4 w-full h-12 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform">
                <Camera size={18} /> Absen Keluar
              </button>
            </>
          )}
        </div>
      </div>

      <AttendanceHistory refreshKey={refreshKey} />
    </div>
  );
}

function AttendanceHistory({ refreshKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/attendance").then(({ data }) => setRows(data.slice(0, 20))); }, [refreshKey]);
  return (
    <div>
      <h2 className="text-lg font-bold text-slate-900 mb-3" style={{fontFamily:'Manrope'}}>Riwayat</h2>
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-slate-500">Belum ada riwayat absensi.</p>}
        {rows.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3">
            {r.clock_in_photo_id && <img src={fileUrl(r.clock_in_photo_id)} className="w-12 h-12 rounded-lg object-cover" alt="" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">{r.date}</p>
              <p className="text-xs text-slate-500">{r.user_name}</p>
              <p className="text-xs text-slate-500">Masuk: {r.clock_in_at ? new Date(r.clock_in_at).toLocaleTimeString('id-ID') : '-'} | Keluar: {r.clock_out_at ? new Date(r.clock_out_at).toLocaleTimeString('id-ID') : '-'}</p>
              {r.is_late && <span className="mt-1 inline-block px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-bold uppercase tracking-wider">Telat {r.late_minutes} mnt</span>}
              {r.clock_in_no_gps && <span className="mt-1 ml-1 inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">Tanpa GPS</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
